import { FC, ReactNode, useCallback, useState } from 'react';
import { ErrorCode as DropzoneErrorCode, FileRejection } from 'react-dropzone';

import ImageDropzone from './ImageDropzone';

import { imageUploadValidator } from '../../helpers/imageUploadValidator';

export type ErrorCode = DropzoneErrorCode | 'invalid-dimensions';

type Props = {
    multi?: boolean;
    imageFiles?: File[];
    fieldRequiredErrorMessage: ReactNode;
    setFormField: (value: File[]) => void;
};

const ImageUpload: FC<Props> = ({
    multi,
    imageFiles = [],
    fieldRequiredErrorMessage,
    setFormField,
}) => {
    const [errors, setErrors] = useState<ErrorCode[]>([]);

    const handleDrop = useCallback(
        async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            setErrors([]);

            if (fileRejections.length) {
                const rejectionErrorCodes = fileRejections.reduce<ErrorCode[]>((acc, value) => {
                    value.errors.forEach(({ code }) => {
                        if (!acc.includes(code as ErrorCode)) {
                            acc.push(code as ErrorCode);
                        }
                    });

                    return acc;
                }, []);

                setErrors(rejectionErrorCodes);
                return;
            }

            if (acceptedFiles.length) {
                const validationErrorCodes = await imageUploadValidator(acceptedFiles);

                if (validationErrorCodes.length) {
                    setErrors(validationErrorCodes);
                    return;
                }

                setFormField([...imageFiles, ...acceptedFiles]);
            }
        },
        [imageFiles, setFormField]
    );

    const handleDelete = (imageFile: File): void => {
        setFormField(imageFiles.filter(imgFile => imgFile !== imageFile));
    };

    return (
        <ImageDropzone
            multi={!!multi}
            imageFiles={imageFiles}
            fieldRequiredErrorMessage={fieldRequiredErrorMessage}
            errors={errors}
            onDrop={handleDrop}
            onDelete={handleDelete}
        />
    );
};

export default ImageUpload;
